import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Box, Checkbox, Menu, MenuItem, Table, TableBody, TableCell, TableHead, TableRow,
  Tooltip, Typography, IconButton, ListItemText, ListItemIcon, Divider,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import TuneIcon from '@mui/icons-material/Tune';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import BarChartIcon from '@mui/icons-material/BarChart';
import {
  FaHourglassHalf, FaClock, FaCircle, FaFlag, FaFolder,
} from 'react-icons/fa';
import {
  CardWrapper, SearchField, ButtonOutlined, formatBRL,
} from '../UI';
import {
  AlertaVencimentoProcesso, AlertaCompromissoProcesso, AlertaProcessoNaoTrabalhado,
  AlertaComRequisicao, AlertaPasta, corSituacao,
} from './AlertasProcesso';
import ResizeHandle from './ResizeHandle';
import {
  PROCESSOS, STATUS_OPTIONS, CONDICAO_OPTIONS, ORDENACAO_OPTIONS, SITUACOES,
} from '../../mocks/processos.mock';
import * as C from '../../theme/colors.constants';

/**
 * Grid de Processos com largura ajustável.
 *
 * Requisito: "opção para o usuário clicar na lateral direita e conseguir
 * aumentar para exibir mais colunas ou diminuir para ocultar as colunas".
 *
 * Como resolvi:
 * - A grid vive num painel cuja largura é estado (`largura`, em px).
 * - Cada coluna declara `min` (largura mínima) e `fixa` (nunca some).
 * - A cada render, percorro as colunas na ordem e vou somando `min`; as que
 *   couberem na largura atual entram em `colunasVisiveis`. Assim, arrastar para
 *   a direita revela colunas progressivamente e arrastar para a esquerda as
 *   oculta — sem nenhum menu de configuração.
 * - O arraste é feito por `ResizeHandle`, que ouve mousemove/mouseup no
 *   `document` (não no elemento) para o cursor não "escapar" durante o drag.
 *
 * As três primeiras colunas (checkbox, Processo, Status) são `fixa`, então o
 * painel nunca chega a um estado ilegível.
 */

const LARGURA_MIN = 430;
const LARGURA_PADRAO = 730; // aproxima o print grid_icons_check.png
const LARGURA_MAX_FALLBACK = 1700;

/** Definição das colunas, na ordem de revelação. */
const COLUNAS = [
  {
    id: 'checkbox', label: '', min: 44, fixa: true, align: 'center',
  },
  {
    id: 'nroProcesso', label: 'Processo', min: 86, fixa: true, align: 'left', ordenavel: true,
  },
  {
    id: 'naoTrabalhado', label: <FaCircle size={9} />, hint: 'Processo não trabalhado', min: 38, align: 'center',
  },
  {
    id: 'compromisso', label: <FaClock size={13} />, hint: 'Compromisso', min: 38, align: 'center',
  },
  {
    id: 'requisicao', label: <FaFlag size={12} />, hint: 'Com requisição', min: 38, align: 'center',
  },
  {
    id: 'pasta', label: <FaFolder size={12} />, hint: 'Pasta', min: 38, align: 'center',
  },
  {
    id: 'valorPrincipal', label: 'Valor Principal', min: 118, align: 'right', ordenavel: true,
  },
  {
    id: 'corAlertaFase', label: <FaHourglassHalf size={13} />, hint: 'Dias na fase', min: 56, align: 'center', ordenavel: true,
  },
  {
    id: 'status', label: 'Status', min: 96, fixa: true, align: 'left', ordenavel: true,
  },
  {
    id: 'situacao', label: 'Situação', min: 175, align: 'left', ordenavel: true,
  },
  {
    id: 'diasComProcesso', label: 'Dias com Processo', min: 92, align: 'center', ordenavel: true,
  },
  {
    id: 'negociador', label: 'Negociador', min: 165, align: 'left',
  },
  {
    id: 'nomeDevedor', label: 'Nome do Devedor', min: 185, align: 'left',
  },
  {
    id: 'nomeCliente', label: 'Nome do Cliente', min: 185, align: 'left',
  },
  {
    id: 'fase', label: 'Fase', min: 105, align: 'left',
  },
  {
    id: 'dataAbertura', label: 'Abertura', min: 92, align: 'center',
  },
];

/** Botão de filtro com menu (Status / Condição / Ordenar). */
function FiltroMenu({
  label, valor, options, onChange, icone, multiplo = false, extra,
}) {
  const [anchor, setAnchor] = useState(null);
  const selecionados = multiplo ? valor || [] : [];

  const escolher = (opt) => {
    if (multiplo) {
      const novo = selecionados.includes(opt)
        ? selecionados.filter(x => x !== opt)
        : [...selecionados, opt];
      onChange(novo);
      return;
    }
    onChange(opt === valor ? '' : opt);
    setAnchor(null);
  };

  // eslint-disable-next-line no-nested-ternary
  const resumo = multiplo
    ? selecionados.length === 0 ? '' : `${selecionados.length} selec.`
    : valor || '';

  return (
    <>
      <Box
        onClick={e => setAnchor(e.currentTarget)}
        role="button"
        tabIndex={0}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setAnchor(e.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          border: `1px solid ${C.borderGrey}`,
          borderRadius: 2,
          backgroundColor: C.white,
          pl: 1.25,
          pr: 0.25,
          py: 0.5,
          minWidth: 96,
          flexShrink: 0,
          '&:hover': { borderColor: C.navy },
        }}
      >
        {icone}
        <Typography
          variant="body2"
          sx={{
            color: resumo ? C.black : C.greyish,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: resumo ? 600 : 400,
          }}
        >
          {resumo || label}
        </Typography>
        <ArrowDropDownIcon sx={{ color: C.navy, fontSize: 20 }} />
      </Box>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { maxHeight: 420, minWidth: 260, boxShadow: C.shadowFloat } }}
      >
        <MenuItem disabled sx={{ opacity: '1 !important' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: C.warmGrey, letterSpacing: 0.4 }}>
            {label.toUpperCase()}
          </Typography>
        </MenuItem>
        <Divider />
        {extra}
        {options.map((opt) => {
          const marcado = multiplo ? selecionados.includes(opt.id ?? opt) : (opt.id ?? opt) === valor;
          const texto = opt.label ?? opt;
          return (
            <MenuItem key={opt.id ?? opt} onClick={() => escolher(opt.id ?? opt)} selected={marcado}>
              <ListItemIcon sx={{ minWidth: 30 }}>
                {marcado ? <CheckIcon fontSize="small" sx={{ color: C.navy }} /> : null}
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ variant: 'body2' }}>{texto}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
FiltroMenu.propTypes = {
  label: PropTypes.string.isRequired,
  valor: PropTypes.any,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  icone: PropTypes.node,
  multiplo: PropTypes.bool,
  extra: PropTypes.node,
};
FiltroMenu.defaultProps = {
  valor: null,
  icone: null,
  multiplo: false,
  extra: null,
};

export default function GridProcessos({ selecionado, onSelecionar }) {
  const [largura, setLargura] = useState(LARGURA_PADRAO);
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState([]);
  const [condicao, setCondicao] = useState([]);
  const [ordenarPor, setOrdenarPor] = useState('nroProcesso');
  const [ordemAsc, setOrdemAsc] = useState(true);
  const [marcados, setMarcados] = useState([]);
  const wrapperRef = useRef(null);
  const [larguraMax, setLarguraMax] = useState(LARGURA_MAX_FALLBACK);

  // O painel não deve passar de ~78% da viewport, senão o painel de detalhe
  // desaparece. Recalculo no resize para acompanhar telas maiores.
  useEffect(() => {
    const calc = () => setLarguraMax(Math.max(LARGURA_MIN + 200, Math.round(window.innerWidth * 0.78)));
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, []);

  // Folga descontada da largura útil: padding horizontal do card (2x até 30px)
  // + a faixa da alça de resize. Sem isso a última coluna "entra" e trunca o
  // próprio cabeçalho.
  const FOLGA = 78;

  const colunasVisiveis = useMemo(() => {
    let acumulado = 0;
    return COLUNAS.filter((c) => {
      if (c.fixa) {
        acumulado += c.min;
        return true;
      }
      if (acumulado + c.min <= largura - FOLGA) {
        acumulado += c.min;
        return true;
      }
      return false;
    });
  }, [largura]);

  const dados = useMemo(() => {
    let lista = [...PROCESSOS];
    if (busca.trim()) lista = lista.filter(p => p.nroProcesso.includes(busca.trim()));
    if (status.length) lista = lista.filter(p => status.includes(p.status));
    if (condicao.length) lista = lista.filter(p => condicao.includes(p.condicao));

    const ordemSituacao = Object.fromEntries(SITUACOES.map(s => [s.id, s.ordem]));
    lista.sort((a, b) => {
      let va = a[ordenarPor];
      let vb = b[ordenarPor];
      if (ordenarPor === 'situacao') {
        va = ordemSituacao[a.situacao] ?? 99;
        vb = ordemSituacao[b.situacao] ?? 99;
      }
      if (ordenarPor === 'nroProcesso') {
        va = Number(a.nroProcesso);
        vb = Number(b.nroProcesso);
      }
      if (typeof va === 'string') return ordemAsc ? va.localeCompare(vb) : vb.localeCompare(va);
      return ordemAsc ? va - vb : vb - va;
    });
    return lista;
  }, [busca, status, condicao, ordenarPor, ordemAsc]);

  const alternarTodos = useCallback(
    e => setMarcados(e.target.checked ? dados.map(d => d.id) : []),
    [dados],
  );

  const renderCelula = (p, col) => {
    switch (col.id) {
      case 'checkbox':
        return (
          <Checkbox
            size="small"
            checked={marcados.includes(p.id)}
            onClick={e => e.stopPropagation()}
            onChange={e => setMarcados(m => (e.target.checked ? [...m, p.id] : m.filter(x => x !== p.id)))
            }
          />
        );
      case 'nroProcesso':
        return (
          <Typography
            sx={{
              fontWeight: 700,
              color: C.navy,
              fontSize: 'clamp(12px, 0.68vw, 15px)',
              textDecoration: selecionado?.id === p.id ? 'underline' : 'none',
            }}
          >
            {p.nroProcesso}
          </Typography>
        );
      case 'naoTrabalhado':
        return <AlertaProcessoNaoTrabalhado ativo={p.naoTrabalhado} />;
      case 'compromisso':
        return <AlertaCompromissoProcesso compromisso={p.compromisso} hint={p.condicao} />;
      case 'requisicao':
        return <AlertaComRequisicao hint={p.requisicao} />;
      case 'pasta':
        return <AlertaPasta pasta={p.pasta} />;
      case 'valorPrincipal':
        return <Typography variant="body2">{formatBRL(p.valorPrincipal)}</Typography>;
      case 'corAlertaFase':
        return <AlertaVencimentoProcesso cor={p.corAlertaFase} dias={p.diasFase} />;
      case 'status':
        return (
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: p.status === 'Ativo' ? C.midGreen : C.navy }}
          >
            {p.status}
          </Typography>
        );
      case 'situacao':
        return (
          <Typography variant="body2" sx={{ color: corSituacao[p.situacao] || C.black, fontWeight: 600 }}>
            {SITUACOES.find(s => s.id === p.situacao)?.label}
            {' '}
            ·
            {p.condicao}
          </Typography>
        );
      default:
        return (
          <Tooltip title={String(p[col.id] ?? '')} arrow>
            <Typography
              variant="body2"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {p[col.id]}
            </Typography>
          </Tooltip>
        );
    }
  };

  return (
    <Box
      ref={wrapperRef}
      sx={{
        position: 'relative',
        flexShrink: 0,
        width: largura,
        minWidth: LARGURA_MIN,
        maxWidth: '100%',
      }}
    >
      <CardWrapper sx={{ pr: 2.5 }}>
        {/* Cabeçalho: título + ações em massa */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5,
        }}
        >
          <Typography variant="h2">Processos</Typography>
          <ButtonOutlined
            size="small"
            startIcon={<PlaylistAddCheckIcon />}
            disabled={marcados.length === 0}
          >
            AÇÕES EM MASSA
            {marcados.length ? ` (${marcados.length})` : ''}
          </ButtonOutlined>
        </Box>

        <SearchField
          placeholder="Pesquisar pelo nº do processo"
          value={busca}
          onChange={e => setBusca(e.target.value)}
          sx={{ mb: 1.5 }}
        />

        {/* Filtros: Status, Condição e Ordenação */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap',
        }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>Status:</Typography>
          <FiltroMenu label="Status" valor={status} options={STATUS_OPTIONS} onChange={setStatus} multiplo />

          <Typography variant="body2" sx={{ fontWeight: 700, ml: 0.5 }}>Condição:</Typography>
          <FiltroMenu label="Condição" valor={condicao} options={CONDICAO_OPTIONS} onChange={setCondicao} multiplo />

          <FiltroMenu
            label="Ordenar por"
            valor={ordenarPor}
            options={ORDENACAO_OPTIONS}
            onChange={v => v && setOrdenarPor(v)}
            icone={<TuneIcon sx={{ fontSize: 16, color: C.navy }} />}
          />
          <Tooltip title={ordemAsc ? 'Ordem crescente' : 'Ordem decrescente'} arrow>
            <IconButton size="small" onClick={() => setOrdemAsc(v => !v)} sx={{ color: C.navy }}>
              {ordemAsc ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Tabela */}
        <Box sx={{ overflowX: 'hidden' }}>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                {colunasVisiveis.map(col => (
                  <TableCell
                    key={col.id}
                    align={col.align}
                    sx={{
                      width: col.min,
                      cursor: col.ordenavel ? 'pointer' : 'default',
                      color: ordenarPor === col.id ? C.navyLight : C.navy,
                    }}
                    onClick={() => {
                      if (!col.ordenavel) return;
                      if (ordenarPor === col.id) setOrdemAsc(v => !v);
                      else setOrdenarPor(col.id);
                    }}
                  >
                    {/* eslint-disable-next-line no-nested-ternary */}
                    {col.id === 'checkbox' ? (
                      <Checkbox
                        size="small"
                        checked={marcados.length > 0 && marcados.length === dados.length}
                        indeterminate={marcados.length > 0 && marcados.length < dados.length}
                        onChange={alternarTodos}
                      />
                    ) : col.hint ? (
                      <Tooltip title={col.hint} arrow>
                        <Box component="span" sx={{ display: 'inline-flex', color: C.navy }}>{col.label}</Box>
                      </Tooltip>
                    ) : (
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'flex-end', gap: 0.25 }}>
                        {col.label}
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {ordenarPor === col.id ? (
                          ordemAsc ? <ArrowUpwardIcon sx={{ fontSize: 13 }} /> : <ArrowDownwardIcon sx={{ fontSize: 13 }} />
                        ) : null}
                      </Box>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {dados.map(p => (
                <TableRow
                  key={p.id}
                  hover
                  selected={selecionado?.id === p.id}
                  onClick={() => onSelecionar && onSelecionar(p)}
                  sx={{
                    cursor: 'pointer',
                    '&.Mui-selected': { backgroundColor: 'rgba(13,43,82,0.06)' },
                    '&.Mui-selected:hover': { backgroundColor: 'rgba(13,43,82,0.10)' },
                  }}
                >
                  {colunasVisiveis.map(col => (
                    <TableCell key={col.id} align={col.align} sx={{ width: col.min, maxWidth: col.min }}>
                      {renderCelula(p, col)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {dados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colunasVisiveis.length} align="center" sx={{ py: 4, color: C.greyish }}>
                    Nenhum processo encontrado com os filtros aplicados.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </Box>

        {/* Rodapé */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, mt: 2, pt: 1.5, borderTop: `1px solid ${C.borderGrey}`,
        }}
        >
          <BarChartIcon sx={{ fontSize: 18, color: C.navy }} />
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Total de processos:
            {' '}
            {dados.length}
          </Typography>
          <Typography variant="caption" sx={{ color: C.warmGrey, ml: 'auto' }}>
            {colunasVisiveis.length - 1}
            {' '}
            de
            {COLUNAS.length - 1}
            {' '}
            colunas visíveis
          </Typography>
        </Box>
      </CardWrapper>

      <ResizeHandle
        largura={largura}
        min={LARGURA_MIN}
        max={larguraMax}
        onChange={setLargura}
        colunasVisiveis={colunasVisiveis.length - 1}
        colunasTotal={COLUNAS.length - 1}
      />
    </Box>
  );
}
GridProcessos.propTypes = {
  selecionado: PropTypes.object,
  onSelecionar: PropTypes.func,
};
GridProcessos.defaultProps = {
  selecionado: null,
  onSelecionar: null,
};
